<template>
  <v-container
    class="bg-white"
    style="max-height: 90vh; overflow-y: auto">
    <v-card variant="text">
      <v-card-item>
        <v-card-title>Kalkulationsraum bearbeiten</v-card-title>
        <template #append>
          <v-fab
            elevation="0"
            icon="mdi-close"
            @click.prevent="$emit('close')"></v-fab>
        </template>
      </v-card-item>

      <v-card-text>
        <v-text-field
          v-model="name"
          label="Name" />
        <v-textarea
          v-model="description"
          label="Beschreibung" />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <DeleteWithConfirmationButton :delete-action="deleteSpace" />
        <v-btn
          elevation="3"
          @click="saveChanges"
          >Speichern</v-btn
        >
      </v-card-actions>
    </v-card>
    <v-divider></v-divider>
    <v-card class="pa-5 ml-10 mr-10 mt-5">
      <v-card flat>
        <v-card-title>Mitarbeiter</v-card-title>
        <v-card-text>
          <v-data-table
            class="border-table"
            density="compact"
            :headers="headers"
            hide-no-data
            :hide-default-footer="
              users.filter((user) => user.state).length < 10
            "
            items-per-page="100"
            item-value="id"
            :items="users.filter((user) => user.state)">
            <template #item.actions="{ item }">
              <v-icon
                v-if="
                  Number(item.id) !== Number(authStore.user.id) &&
                  item.role !== 'organization'
                "
                color="grey"
                size="large"
                :data-cy="item.name + '-embedded-delete'"
                @click="deleteWithConfirmation(item.id)">
                mdi-delete-outline
              </v-icon>
              <span v-if="item.role === 'organization'">(Team)</span>
            </template>
            <template #item.rights="{ item, value }">
              <template v-if="value === 'Admin'"> {{ value }}</template>
              <v-select
                v-else
                :model-value="item.rights"
                @update:model-value="changeUserRights(item.id, $event)"
                :items="newMemberRoleOptions"></v-select>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-card flat>
        <v-card-title>Eingeladen</v-card-title>
        <v-card-text>
          <v-data-table
            class="border-table"
            density="compact"
            :headers="inviteHeaders"
            hide-no-data
            :hide-default-footer="
              users.filter((user) => !user.state).length < 10
            "
            item-value="id"
            :items="users.filter((user) => !user.state)">
            <template #item.actions="{ item }">
              <v-icon
                v-if="Number(item.id) !== Number(authStore.user.id)"
                color="grey"
                size="large"
                :data-cy="item.name + '-embedded-delete'"
                @click="deleteWithConfirmation(item.id, item.pending_id)">
                mdi-delete-outline
              </v-icon>
            </template>
            <template
              #body.append
              v-if="space.role !== 'initial'">
              <tr>
                <td>
                  <v-text-field
                    ref="newMemberEmailInput"
                    :rules="[...emailRules, existsAlready]"
                    v-model="newMemberEmail"
                    placeholder="Nutzer Email"></v-text-field>
                </td>
                <td>
                  <v-select
                    v-model="newMemberRole"
                    :items="newMemberRoleOptions"></v-select>
                </td>
                <td>
                  <v-btn
                    :disabled="!newMemberEmailInput?.isValid"
                    @click="addMember"
                    >Hinzufügen</v-btn
                  >
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-card>
  </v-container>
</template>

<script lang="ts" setup>
import { useSpaceStore } from "@/stores/spaceStore";
import axios from "axios";
import { useTranslation } from "i18next-vue";
import { onMounted } from "vue";
import { computed } from "vue";
import { ref } from "vue";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import { SPACE_RIGHT_WRITE, spaceRightNames } from "@/utils/constants";
import { useMainStore } from "@/stores/mainStore";
import { useAuthStore } from "@/stores/authStore";
import { SPACE_RIGHT_READ } from "@/utils/constants";
import DeleteWithConfirmationButton from "../tools/DeleteWithConfirmationButton.vue";
import { useUserRules } from "@/validations/user.js";
import { useTemplateRef } from "vue";

const { t } = useTranslation();
const { emailRules, passwordRules } = useUserRules(t);
const createConfirm = useConfirm();
const createSnackbar = useSnackbar();

const props = defineProps({
  spaceId: {
    type: String,
    required: true
  }
});

const emits = defineEmits(["close"]);

const name = ref("");
const description = ref("");

const existsAlready = (v: string) => {
  return (
    users.value.findIndex((user) => user.email === v) == -1 ||
    t("validations.exists-in-space")
  );
};

const newMemberEmailInput = useTemplateRef("newMemberEmailInput");
const newMemberEmail = ref("");
const newMemberRole = ref(SPACE_RIGHT_READ);

const newMemberRoleOptions = ref([
  { title: "Lesen", value: SPACE_RIGHT_READ },
  { title: "Schreiben", value: SPACE_RIGHT_WRITE }
]);

const users = ref([]);

const spaceStore = useSpaceStore();
const mainStore = useMainStore();
const authStore = useAuthStore();

const headers: any = computed(() => [
  {
    title: "Name",
    align: "start",
    sortable: false,
    key: "name",
    value: (item) => item.first_name + " " + item.last_name,
    width: "25%"
  },
  {
    title: "Email",
    key: "email",
    sortable: false,
    width: "25%"
  },
  {
    title: "Rechte",
    key: "rights",
    sortable: false,
    value: (item) => spaceRightNames[item.rights],
    width: "25%"
  },
  {
    title: "",
    key: "actions",
    sortable: false,
    align: "end",
    width: "25%"
  }
]);

const inviteHeaders: any = computed(() => [
  {
    title: "Email",
    key: "email",
    sortable: false,
    width: "25%"
  },
  {
    title: "",
    key: "actions",
    sortable: false,
    align: "end",
    width: "25%"
  }
]);

onMounted(() => {
  listUsers();
  name.value = space.value.name;
  description.value = space.value.description;
});

const space = computed({
  get() {
    return mainStore.calculationSpaces.find(
      (space) => space.id === props.spaceId
    );
  },
  set(value) {
    const spaceIndex = mainStore.calculationSpaces.findIndex(
      (space) => space.id === props.spaceId
    );
    mainStore.calculationSpaces[spaceIndex].name = name.value;
    mainStore.calculationSpaces[spaceIndex].description = description.value;
  }
});

function listUsers() {
  axios
    .get(`/api/calculation-spaces/${props.spaceId}/users`)
    .then((result) => {
      users.value = result.data;
    })
    .catch((error) => console.error);
}

const writeUserCount = computed(() => {
  return users.value.filter((user) => user.rights & SPACE_RIGHT_WRITE).length;
});

function saveChanges() {
  axios
    .put(`/api/calculation-spaces/${props.spaceId}`, {
      name: name.value,
      description: description.value
    })
    .then((result) => {
      space.value = result.data;
      createSnackbar({
        text: t("actions.success.space"),
        showCloseButton: false,
        snackbarProps: { color: "success", timeout: 1000 }
      });
    })
    .catch((error) => console.error);
}

function changeUserRights(id: string, rights: number) {
  console.log(id, rights);
  createConfirm({
    dialogProps: { width: 400 },
    cardProps: { color: "success" },
    title: t("actions.confirmation.change-rights-user.title"),
    content: t("actions.confirmation.change-rights-user.content"),
    confirmationText: t("actions.confirmation.change-rights-user.confirm"),
    cancellationText: t("actions.confirmation.change-rights-user.cancel"),
    cancellationButtonProps: {
      // @ts-ignore
      "data-cy": "import-cancel-button"
    },
    confirmationButtonProps: {
      color: "warning",
      // @ts-ignore
      "data-cy": "import-confirm-button"
    }
  }).then((confirm) => {
    if (confirm) {
      axios
        .post(`/api/calculation-spaces/${props.spaceId}/change-user-rights`, {
          id,
          rights
        })
        .then((_result) => {
          listUsers();
        })
        .catch((_error) => console.error);
      // createSnackbar({
      //   text: t("actions.deleted"),
      //   showCloseButton: false,
      //   snackbarProps: { color: "error", timeout: 1000 }
      // });
    }
  });
}

function deleteWithConfirmation(id: string | null, pending_id: string) {
  createConfirm({
    dialogProps: { width: 400 },
    cardProps: { color: "error" },
    title: t("actions.confirmation.delete.title"),
    content: t("actions.confirmation.delete.content"),
    confirmationText: t("actions.confirmation.delete.confirm"),
    cancellationText: t("actions.confirmation.delete.cancel"),
    cancellationButtonProps: {
      // @ts-ignore
      "data-cy": "delete-cancel-button"
    },
    confirmationButtonProps: {
      color: "warning",
      // @ts-ignore
      "data-cy": "delete-confirm-button"
    }
  }).then((confirm) => {
    if (confirm) {
      deleteUserFromSpace(id, pending_id).then(() => {
        createSnackbar({
          text: t("actions.deleted"),
          showCloseButton: false,
          snackbarProps: { color: "error", timeout: 1000 }
        });
      });
    }
  });
}

async function deleteUserFromSpace(id: string | null, pending_id: string) {
  // return console.log("deleteBackup");
  if (id === null) {
    return axios
      .post(`/api/calculation-spaces/${props.spaceId}/delete-invite`, {
        pending_id: pending_id
      })
      .then((result) => {
        users.value = users.value.filter(
          (user) => user.pending_id !== pending_id
        );
      })
      .catch((error) => console.error);
  } else {
    return axios
      .post(`/api/calculation-spaces/${props.spaceId}/delete-user`, {
        id: id
      })
      .then((result) => {
        users.value = users.value.filter((user) => user.id !== id);
      })
      .catch((error) => console.error);
  }
}

async function addMember() {
  if (!newMemberEmail || !newMemberRole) return;
  axios
    .post(`/api/calculation-spaces/${props.spaceId}/add-user`, {
      email: newMemberEmail.value,
      rights: newMemberRole.value,
      roomName: name.value,
      inviterName: authStore.user.first_name + " " + authStore.user.last_name
    })
    .then((response) => {
      users.value.push(response.data);
      newMemberEmail.value = "";
      newMemberRole.value = SPACE_RIGHT_READ;
    })
    .catch(console.error);
}

function deleteSpace() {
  axios
    .delete(`/api/calculation-spaces/${props.spaceId}`)
    .then(() => {
      mainStore.loadCalculationSpaces();
      emits("close");
      createSnackbar({
        text: t("actions.deleted"),
        showCloseButton: false,
        snackbarProps: { color: "error", timeout: 1000 }
      });
    })
    .catch((error) => {
      console.error(error);
      createSnackbar({
        text: t("actions.error"),
        showCloseButton: false,
        snackbarProps: { color: "error", timeout: 1000 }
      });
    });
}
</script>
